import React, { useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import Col from "antd/es/col";
import Form from "antd/es/form";
import { useForm, useWatch } from "antd/es/form/Form";
import Radio from "antd/es/radio";
import Row from "antd/es/row";
import Tabs from "antd/es/tabs";
import cn from "classnames";
import { useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared/enum";
import DuplicateRunStatus from "@mapmycustomers/shared/enum/DuplicateRunStatus";
import Organization from "@mapmycustomers/shared/types/Organization";
import Setting from "@mapmycustomers/shared/types/Setting";
import { isDefined } from "@mapmycustomers/shared/util/assert";
import { Alert, Button, Modal, Popconfirm } from "@mapmycustomers/ui";

import Checkbox from "@app/component/input/Checkbox";
import RadioGroup from "@app/component/input/RadioGroup";
import OrganizationSetting from "@app/enum/OrganizationSetting";
import { getLastDupeRunStatus } from "@app/scene/settings/component/MergeDuplicates/store";
import { recalculateDuplicates } from "@app/scene/settings/component/MergeDuplicates/store/actions";
import { getOrganizationId, getOrganizationSettings, isOrganizationUpdating } from "@app/store/iam";
import { updateOrganization } from "@app/store/iam/actions";
import { RootState } from "@app/store/rootReducer";
import layout from "@app/styles/layout";
import DuplicateCriteriaSetting from "@app/types/duplicates/DuplicateCriteriaSetting";
import useFormChangeTracker from "@app/util/hook/form/useFormChangeTracker";

import styles from "./DuplicateCriteriaModal.module.scss";
import messages from "./messages";

interface Props {
  defaultEntityType: EntityType;
  loading: boolean;
  onHide: () => void;
  onRecalculateDuplicates: typeof recalculateDuplicates.request;
  organizationId?: Organization["id"];
  organizationSettings?: Setting[];
  recalculatingInProgress: boolean;
  updateOrganization: typeof updateOrganization.request;
}

const DuplicateCriteriaModal: React.FC<Props> = ({
  defaultEntityType,
  loading,
  onHide,
  onRecalculateDuplicates,
  organizationId,
  organizationSettings,
  recalculatingInProgress,
  updateOrganization,
}) => {
  const intl = useIntl();
  const [activeTab, setActiveTab] = useState<EntityType>(defaultEntityType);
  const [form] = useForm<DuplicateCriteriaSetting>();
  const [isFormChanged, handleFormChanges] = useFormChangeTracker(form);
  const criteriaSetting = useMemo(
    () => organizationSettings?.find((s) => s.key === OrganizationSetting.DUPLICATE_CHECK_CRITERIA),
    [organizationSettings]
  );

  const companyNameValue: boolean = useWatch([EntityType.COMPANY, "name"], form);
  const personNameValue: boolean = useWatch([EntityType.PERSON, "name"], form);
  const companyWebsiteValue: boolean = useWatch([EntityType.COMPANY, "website"], form);

  const handleConfirm = useCallback(() => {
    const criteria = form.getFieldsValue();
    if (organizationId) {
      updateOrganization({
        callback: () => {
          onHide();
          onRecalculateDuplicates();
        },
        organization: { id: organizationId },
        settings: [
          criteriaSetting && criteria ? { ...criteriaSetting, value: criteria } : undefined,
        ].filter(isDefined),
      });
    }
  }, [form, onRecalculateDuplicates, criteriaSetting, organizationId, updateOrganization, onHide]);

  const okDisabled = !isFormChanged || loading;

  return (
    <Modal footer={null} onCancel={onHide} open title={intl.formatMessage(messages.title)}>
      {recalculatingInProgress && (
        <Alert
          className={styles.recalculatingInProgress}
          message={intl.formatMessage(messages.recalculatingInProgress)}
          showIcon
          type="warning"
        />
      )}
      <Tabs activeKey={activeTab} onChange={(tab) => setActiveTab(tab as EntityType)}>
        <Tabs.TabPane key={EntityType.COMPANY} tab={intl.formatMessage(messages.companies)} />
        <Tabs.TabPane key={EntityType.PERSON} tab={intl.formatMessage(messages.people)} />
      </Tabs>
      <Form
        form={form}
        initialValues={criteriaSetting?.value}
        onValuesChange={handleFormChanges}
        preserve
      >
        {[EntityType.COMPANY, EntityType.PERSON].map((entityType) => (
          <div
            className={cn(styles.fields, { [styles.hidden]: entityType !== activeTab })}
            key={entityType}
          >
            <div className={styles.line}>
              <div>
                <div className={styles.fieldName}>{intl.formatMessage(messages.name)}</div>
                <Form.Item className={styles.formItem} name={[entityType, "nameInRadius"]}>
                  <RadioGroup<boolean>
                    buttonStyle="solid"
                    disabled={
                      !(entityType === EntityType.COMPANY ? companyNameValue : personNameValue) ||
                      recalculatingInProgress
                    }
                  >
                    <Radio.Button value={true}>
                      {intl.formatMessage(messages.nameInRadius)}
                    </Radio.Button>
                    <Radio.Button value={false}>
                      {intl.formatMessage(messages.nameAlways)}
                    </Radio.Button>
                  </RadioGroup>
                </Form.Item>
              </div>
              <Form.Item
                className={styles.formItem}
                name={[entityType, "name"]}
                valuePropName="checked"
              >
                <Checkbox disabled={recalculatingInProgress} />
              </Form.Item>
            </div>
            <div className={styles.line}>
              <div className={styles.fieldName}>{intl.formatMessage(messages.location)}</div>
              <Form.Item
                className={styles.formItem}
                name={[entityType, "address"]}
                valuePropName="checked"
              >
                <Checkbox disabled={recalculatingInProgress} />
              </Form.Item>
            </div>
            <div className={styles.line}>
              <div className={styles.fieldName}>{intl.formatMessage(messages.phone)}</div>
              <Form.Item
                className={styles.formItem}
                name={[entityType, "phone"]}
                valuePropName="checked"
              >
                <Checkbox disabled={recalculatingInProgress} />
              </Form.Item>
            </div>
            <div className={styles.line}>
              <div className={styles.fieldName}>{intl.formatMessage(messages.email)}</div>
              <Form.Item
                className={styles.formItem}
                name={[entityType, "email"]}
                valuePropName="checked"
              >
                <Checkbox disabled={recalculatingInProgress} />
              </Form.Item>
            </div>
            {entityType === EntityType.COMPANY && (
              <div className={styles.line}>
                <div>
                  <div className={styles.fieldName}>{intl.formatMessage(messages.website)}</div>
                  <Form.Item className={styles.formItem} name={[entityType, "domain"]}>
                    <RadioGroup<boolean>
                      buttonStyle="solid"
                      disabled={!companyWebsiteValue || recalculatingInProgress}
                    >
                      <Radio.Button value={true}>
                        {intl.formatMessage(messages.domain)}
                      </Radio.Button>
                      <Radio.Button value={false}>
                        {intl.formatMessage(messages.fullUrl)}
                      </Radio.Button>
                    </RadioGroup>
                  </Form.Item>
                </div>

                <Form.Item
                  className={styles.formItem}
                  name={[entityType, "website"]}
                  valuePropName="checked"
                >
                  <Checkbox disabled={recalculatingInProgress} />
                </Form.Item>
              </div>
            )}
          </div>
        ))}
        <Row className={styles.footer} gutter={layout.spacerS} justify="end">
          <Col>
            <Button onClick={onHide}>{intl.formatMessage(messages.cancel)}</Button>
          </Col>
          <Col>
            <Popconfirm
              cancelText={intl.formatMessage(messages.confirmCancel)}
              description={intl.formatMessage(messages.confirm, { b: (text) => <b>{text}</b> })}
              okText={intl.formatMessage(messages.confirmOk)}
              onConfirm={handleConfirm}
              placement="left"
            >
              <Button
                className={cn({ [styles.disabledOk]: okDisabled })}
                disabled={okDisabled}
                loading={loading}
                type="primary"
              >
                {intl.formatMessage(messages.okText)}
              </Button>
            </Popconfirm>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => ({
  loading: isOrganizationUpdating(state),
  organizationId: getOrganizationId(state),
  organizationSettings: getOrganizationSettings(state),
  recalculatingInProgress: getLastDupeRunStatus(state) === DuplicateRunStatus.ONGOING,
});

const mapDispatchToProps = {
  onRecalculateDuplicates: recalculateDuplicates.request,
  updateOrganization: updateOrganization.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(DuplicateCriteriaModal);
